"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { effectiveCartPlacement, normalizeCartDisplay } from "../presentation";
import { getHeaderState, getServerHeaderState, subscribeHeaderState, publishHeaderState, resetHeaderState } from "./cart-header-state";
import { useWebshopT } from "./i18n";
import { StorefrontIcon } from "./StorefrontIcon";
import { publishStorefrontPreferences } from "./storefront-preferences-state";
import { cleanShopPath, requestCart } from "./storefront-api";
import { safeCheckoutPath } from "./checkout-path";
import { storefrontErrorMessage } from "./storefront-errors";
import type { AddToCartEventDetail } from "./storefront";
import { formatMoney, normalizeCart, type StorefrontCart } from "./types";

declare global {
  interface WindowEventMap {
    "webshops:open-cart": CustomEvent;
    "webshops:store-active": CustomEvent<boolean>;
    "webshops:cart-pending": CustomEvent<{ productId: string; pending: boolean }>;
  }
}

export type MiniCartProps = {
  headerProvided?: boolean;
  apiBasePath?: string;
  shopPath?: string;
  locale?: string;
};

function EmptyCart({ message }: { message: string }) {
  return (
    <div className="webshops-mini-cart__empty">
      <span className="material-symbols-outlined" aria-hidden="true">shopping_bag</span>
      <h3>{message}</h3>
    </div>
  );
}

function currentLocaleShopBase(shopPath: string, locale?: string): string {
  const clean = cleanShopPath(shopPath);
  if (typeof window === "undefined") return `/${clean}`;
  const firstPathSegment = window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  const normalizedLocale = locale?.toLowerCase();
  // siteComponent receives only a locale, not the server route props. The
  // current CMS path is therefore the authoritative signal for a non-default
  // locale prefix; never synthesize one from user-controlled cart data.
  if (normalizedLocale && firstPathSegment === normalizedLocale && !clean.toLowerCase().startsWith(`${normalizedLocale}/`)) {
    return `/${normalizedLocale}/${clean}`;
  }
  return `/${clean}`;
}

export function MiniCart({ apiBasePath, shopPath = "shop", locale, headerProvided = false }: MiniCartProps) {
  const t = useWebshopT(locale);
  const [cart, setCart] = useState<StorefrontCart>(() => normalizeCart(null));
  const [display, setDisplay] = useState(() => normalizeCartDisplay(null));
  const placement = effectiveCartPlacement(display, headerProvided);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingItem, setPendingItem] = useState<string | null>(null);
  const [error, setErrorState] = useState<string | null>(null);
  // Reloading only helps after a failed load, not after a rejected action.
  const [retryable, setRetryable] = useState(false);
  const setError = useCallback((message: string | null) => { setRetryable(false); setErrorState(message); }, []);
  const [storeActive, setStoreActive] = useState(true);
  const openerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const mountedRef = useRef(true);
  const shopBase = currentLocaleShopBase(shopPath, locale);
  const checkoutHref = safeCheckoutPath(cart.checkoutUrl) ?? `${shopBase}/checkout`;

  const applyCart = useCallback((nextCart: StorefrontCart) => {
    if (!mountedRef.current) return;
    setCart(nextCart);
    // Mutations from older servers omit display preferences; keep the last GET value.
    if (nextCart.display) setDisplay(nextCart.display);
    if (nextCart.features) publishStorefrontPreferences(nextCart.features);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyCart(await requestCart("GET", {}, undefined, apiBasePath, locale));
    } catch (requestError) {
      if (mountedRef.current) {
        if ((requestError as { code?: string }).code === "store_inactive") {
          publishStorefrontPreferences({ wishlist: false });
          setStoreActive(false);
          window.dispatchEvent(new CustomEvent("webshops:store-active", { detail: false }));
          return;
        }
        setErrorState(storefrontErrorMessage(
          requestError,
          t,
          t("module.webshops.error.network", "Could not reach the shop. Please try again."),
        ));
        setRetryable(true);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [apiBasePath, applyCart, locale, setError, t]);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  useEffect(() => {
    const addToCart = async (event: CustomEvent<AddToCartEventDetail>) => {
      const { productId, variantId, quantity, configuration } = event.detail;
      if (!productId || !Number.isFinite(quantity) || quantity < 1) return;

      setOpen(true);
      setError(null);
      setPendingItem(productId);
      window.dispatchEvent(new CustomEvent("webshops:cart-pending", { detail: { productId, pending: true } }));
      try {
        const requestLocale = event.detail.locale ?? locale ?? "en";
        applyCart(
          await requestCart(
            "POST",
            {
              productId,
              ...(variantId ? { variantId } : {}),
              quantity: Math.max(1, Math.trunc(quantity)),
              ...(configuration ? { configuration } : {}),
            },
            undefined,
            apiBasePath,
            requestLocale,
          ),
        );
      } catch (requestError) {
        if (mountedRef.current) {
          setError(storefrontErrorMessage(
            requestError,
            t,
            t("module.webshops.error.generic", "Something went wrong. Please try again."),
          ));
        }
      } finally {
        window.dispatchEvent(new CustomEvent("webshops:cart-pending", { detail: { productId, pending: false } }));
        if (mountedRef.current) setPendingItem(null);
      }
    };

    window.addEventListener("webshops:add-to-cart", addToCart);
    return () => window.removeEventListener("webshops:add-to-cart", addToCart);
  }, [apiBasePath, applyCart, locale, t]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      } else if (event.key === "Tab") {
        const focusable = Array.from(
          drawerRef.current?.querySelectorAll<HTMLElement>(
            'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
          ) ?? [],
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      (previouslyFocused ?? openerRef.current)?.focus();
    };
  }, [open]);

  useEffect(() => {
    const openCart = () => setOpen(true);
    const cartUpdated = (event: CustomEvent<StorefrontCart>) => {
      setCart(event.detail);
      if (event.detail.display) setDisplay(event.detail.display);
      if (event.detail.features) publishStorefrontPreferences(event.detail.features);
    };
    window.addEventListener("webshops:open-cart", openCart);
    window.addEventListener("webshops:cart-updated", cartUpdated);
    return () => {
      window.removeEventListener("webshops:open-cart", openCart);
      window.removeEventListener("webshops:cart-updated", cartUpdated);
    };
  }, []);

  const changeQuantity = async (itemId: string, quantity: number) => {
    if (!Number.isFinite(quantity) || quantity < 1) return;
    setPendingItem(itemId);
    setError(null);
    try {
      applyCart(await requestCart("PATCH", { itemId, quantity: Math.trunc(quantity) }, undefined, apiBasePath, locale));
    } catch (requestError) {
      if (mountedRef.current) {
        setError(storefrontErrorMessage(
          requestError,
          t,
          t("module.webshops.error.generic", "Something went wrong. Please try again."),
        ));
      }
    } finally {
      if (mountedRef.current) setPendingItem(null);
    }
  };

  const removeItem = async (itemId: string) => {
    setPendingItem(itemId);
    setError(null);
    try {
      applyCart(await requestCart("DELETE", { itemId }, itemId, apiBasePath, locale));
    } catch (requestError) {
      if (mountedRef.current) {
        setError(storefrontErrorMessage(
          requestError,
          t,
          t("module.webshops.error.generic", "Something went wrong. Please try again."),
        ));
      }
    } finally {
      if (mountedRef.current) setPendingItem(null);
    }
  };

  useEffect(() => {
    publishHeaderState({ visible: storeActive && placement === "header", count: cart.itemCount, open });
  }, [storeActive, placement, cart.itemCount, open]);
  useEffect(() => resetHeaderState, []);

  if (!storeActive) return null;
  return (
    <div className="webshops-mini-cart" data-position={display.position} data-open={open ? "true" : "false"}>
      {placement === "floating" && <button
        ref={openerRef}
        className="webshops-mini-cart__trigger"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="webshops-mini-cart-dialog"
        onClick={() => setOpen(true)}
      >
        <StorefrontIcon name="cart" />
        <span className="webshops-mini-cart__trigger-label">{t("module.webshops.cart.title", "Cart")}</span>
        <span className="webshops-mini-cart__count">{cart.itemCount > 99 ? "99+" : cart.itemCount}</span>
      </button>}

      {open ? (
        <>
          <button className="webshops-mini-cart__backdrop" type="button" aria-label={t("module.webshops.cart.close", "Close cart")} onClick={() => setOpen(false)} />
          <aside ref={drawerRef} className="webshops-mini-cart__drawer" id="webshops-mini-cart-dialog" role="dialog" aria-modal="true" aria-labelledby="webshops-mini-cart-title">
            <header className="webshops-mini-cart__header">
              <h2 id="webshops-mini-cart-title">{t("module.webshops.cart.title", "Cart")}</h2>
              <button ref={closeRef} className="webshops-icon-button" type="button" aria-label={t("module.webshops.cart.close", "Close cart")} onClick={() => setOpen(false)}>
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </header>

            {error ? (
              <div className="webshops-notice webshops-notice--error" role="alert">
                <span>{error}</span>
                {retryable ? <button type="button" onClick={() => void refresh()}>{t("module.webshops.admin.retry", "Try again")}</button> : null}
              </div>
            ) : null}

            <div className="webshops-mini-cart__body" aria-busy={loading || pendingItem !== null}>
              {loading ? <p className="webshops-loading">{t("module.webshops.admin.loading", "Loading…")}</p> : null}
              {!loading && cart.items.length === 0 ? <EmptyCart message={t("module.webshops.cart.empty", "Your cart is empty.")} /> : null}
              {!loading && cart.items.length ? (
                <ul className="webshops-mini-cart__items">
                  {cart.items.map((item) => {
                    const isPending = pendingItem === item.id || pendingItem === item.productId;
                    return (
                      <li className="webshops-mini-cart__item" key={item.id}>
                        <span className="webshops-mini-cart__item-image">
                          {item.imageUrl ? <img src={item.imageUrl} alt={item.name} loading="lazy" decoding="async" /> : <span className="material-symbols-outlined" aria-hidden="true">inventory_2</span>}
                        </span>
                        <div className="webshops-mini-cart__item-content">
                          <strong>{item.url ? <a href={item.url}>{item.name}</a> : item.name}</strong>
                          {item.optionValues && Object.keys(item.optionValues).length ? (
                            <small>{Object.entries(item.optionValues).map(([label, value]) => `${label}: ${value}`).join(" · ")}</small>
                          ) : null}
                          <span>{formatMoney(item.lineTotal ?? (item.unitPrice !== undefined ? item.unitPrice * item.quantity : undefined), item.currency ?? cart.currency, locale, cart.currencyMinorDigits)}</span>
                          <div className="webshops-quantity-control" aria-label={`${t("module.webshops.cart.quantity", "Quantity")}: ${item.name}`}>
                            <button type="button" aria-label={`− ${item.name}`} disabled={isPending || item.quantity <= 1} onClick={() => void changeQuantity(item.id, item.quantity - 1)}>−</button>
                            <output aria-live="polite">{item.quantity}</output>
                            <button type="button" aria-label={`+ ${item.name}`} disabled={isPending || item.quantity >= maximumCartQuantity(item)} onClick={() => void changeQuantity(item.id, item.quantity + 1)}>+</button>
                          </div>
                          <button className="webshops-link-button" type="button" disabled={isPending} onClick={() => void removeItem(item.id)}>
                            {t("module.webshops.cart.remove", "Remove")}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>

            {cart.items.length ? (
              <footer className="webshops-mini-cart__footer">
                <dl>
                  {cart.subtotal !== undefined ? <div><dt>{t("module.webshops.cart.subtotal", "Subtotal")}</dt><dd>{formatMoney(cart.subtotal, cart.currency, locale, cart.currencyMinorDigits)}</dd></div> : null}
                  {cart.discountTotal !== undefined && cart.discountTotal > 0 ? <div><dt>{t("module.webshops.cart.discount", "Discount")}</dt><dd>− {formatMoney(cart.discountTotal, cart.currency, locale, cart.currencyMinorDigits)}</dd></div> : null}
                  {cart.shippingTotal !== undefined ? <div><dt>{t("module.webshops.cart.shipping", "Shipping")}</dt><dd>{formatMoney(cart.shippingTotal, cart.currency, locale, cart.currencyMinorDigits)}</dd></div> : null}
                  <div className="webshops-mini-cart__total"><dt>{t("module.webshops.cart.total", "Total")}</dt><dd>{formatMoney(cart.total ?? cart.subtotal, cart.currency, locale, cart.currencyMinorDigits)}</dd></div>
                </dl>
                {cart.couponInvalid ? (
                  <>
                    <p className="webshops-notice webshops-notice--error">{t("module.webshops.coupon.invalid_applied", "This coupon is no longer valid. Remove it before checkout.")}</p>
                    <button className="webshops-button webshops-button--primary webshops-button--wide" type="button" disabled>{t("module.webshops.cart.checkout", "Checkout")}</button>
                  </>
                ) : (
                  <a className="webshops-button webshops-button--primary webshops-button--wide" href={checkoutHref}>
                    {t("module.webshops.cart.checkout", "Checkout")}
                  </a>
                )}
              </footer>
            ) : null}
          </aside>
        </>
      ) : null}
    </div>
  );
}


/** Public, supported header trigger; the site component owns cart requests and the dialog. */
export function HeaderCartTrigger({ locale }: { locale: string }) {
  const t = useWebshopT(locale);
  const { visible, count, open } = useSyncExternalStore(subscribeHeaderState, getHeaderState, getServerHeaderState);
  if (!visible) return null;
  return <button className="webshops-header-cart" type="button" aria-expanded={open} aria-haspopup="dialog" aria-controls="webshops-mini-cart-dialog"
    onClick={() => window.dispatchEvent(new CustomEvent("webshops:open-cart"))}>
    <StorefrontIcon name="cart" />
    <span className="webshops-visually-hidden">{t("module.webshops.cart.title", "Cart")}</span>
    <span className="webshops-header-cart__count">{count > 99 ? "99+" : count}</span>
  </button>;
}

/** Same limit as the cart page: sold individually, otherwise available stock unless backorders are allowed. */
function maximumCartQuantity(item: StorefrontCart["items"][number]): number {
  if (item.soldIndividually) return 1;
  if (!item.allowBackorder && item.stockQuantity !== undefined) return Math.max(1, Math.trunc(item.stockQuantity));
  return 999;
}
